import { type FC, useEffect, useMemo, useState } from "react";
import { Platform, Pressable, useWindowDimensions, View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { startOfDay, endOfDay } from "date-fns";
import {
  MultiSelect,
  DateRangePicker,
  Autocomplete,
  Input,
  type DateRange,
  Text,
  Icon,
} from "@visioni/a3s-ui/components";
import type { ActivityKind } from "@visioni/a3s-activity-recorder/types";
import type { UseActivityFiltersReturn } from "../../hooks/use-activity-filters";
import { activityKindToShortLabel, filterableActivityKinds } from "../../helpers";

const MIN_DATE = new Date(2000, 0, 1);

export interface SiteOption {
  readonly id: string;
  readonly name: string;
}

export interface CameraOption {
  readonly key: string;
  readonly name: string;
  readonly siteId: string;
}

export interface ActivityFilterRowProps {
  readonly filters: UseActivityFiltersReturn;
  readonly sites: readonly SiteOption[];
  readonly cameras: readonly CameraOption[];
  readonly sitesLoading?: boolean | undefined;
  readonly camerasLoading?: boolean | undefined;
  /** Support-only activity kinds (see SUPPORT_ONLY_ACTIVITY_KINDS) are filterable only by support users. */
  readonly isSupportUser?: boolean | undefined;
}

export const ActivityFilterRow: FC<ActivityFilterRowProps> = ({
  filters,
  sites,
  cameras,
  sitesLoading = false,
  camerasLoading = false,
  isSupportUser = false,
}) => {
  const { theme } = useUnistyles();
  const { width } = useWindowDimensions();
  const activityKindOptions = useMemo(() => filterableActivityKinds(isSupportUser), [isSupportUser]);
  const isCompact = width < 800;
  const { filters: draft, updateFilter, setKinds } = filters;
  const [emailDraft, setEmailDraft] = useState(draft.userEmail ?? "");
  const [expanded, setExpanded] = useState(!isCompact);
  const today = useMemo(() => startOfDay(new Date()), []);

  useEffect(() => {
    setEmailDraft(draft.userEmail ?? "");
  }, [draft.userEmail]);

  const activeFilterCount = [
    draft.siteId,
    draft.cameraKey,
    draft.kinds.length > 0,
    draft.startDate,
    draft.userEmail,
  ].filter(Boolean).length;

  const dateRange: DateRange = {
    start: draft.startDate ? new Date(draft.startDate) : null,
    end: draft.endDate ? new Date(draft.endDate) : null,
  };

  const handleDateChange = (range: DateRange) => {
    updateFilter("startDate", range.start ? startOfDay(range.start).toISOString() : null);
    updateFilter("endDate", range.end ? endOfDay(range.end).toISOString() : null);
  };

  const selectedSite = sites.find((s) => s.id === draft.siteId) ?? null;
  const selectedCamera = cameras.find((c) => c.key === draft.cameraKey) ?? null;

  return (
    <View style={styles.container}>
      {isCompact ?
        <Pressable onPress={() => setExpanded((prev) => !prev)} style={styles.toggleBar}>
          <Text textStyle="xs/medium" color="text">
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount.toString()})` : ""}
          </Text>
          <Icon name={expanded ? "chevronUp" : "chevronDown"} size={20} color={theme.colors.textMuted} />
        </Pressable>
      : null}

      {!isCompact || expanded ?
        <View style={styles.row}>
          {sites.length > 1 || sitesLoading ?
            <View style={styles.field}>
              <Autocomplete<SiteOption>
                options={sites}
                value={selectedSite}
                onChange={(site) => updateFilter("siteId", site?.id ?? null)}
                getOptionLabel={(site) => site.name}
                getOptionId={(site) => site.id}
                placeholder="All sites"
                label="Site"
                isLoading={sitesLoading}
              />
            </View>
          : null}

          <View style={styles.field}>
            <Autocomplete<CameraOption>
              options={cameras}
              value={selectedCamera}
              onChange={(camera) => updateFilter("cameraKey", camera?.key ?? null)}
              getOptionLabel={(camera) => camera.name}
              getOptionId={(camera) => camera.key}
              placeholder="All cameras"
              label="Camera"
              isLoading={camerasLoading}
            />
          </View>

          <View style={styles.field}>
            <MultiSelect<ActivityKind>
              options={activityKindOptions}
              value={draft.kinds as ActivityKind[]}
              onChange={(value) => setKinds(value)}
              getOptionLabel={activityKindToShortLabel}
              getOptionId={(kind) => kind}
              placeholder="All activities"
              label="Activity type"
              maxTags={1}
            />
          </View>

          <View style={styles.field}>
            <DateRangePicker
              value={dateRange}
              onChange={handleDateChange}
              label="Date range"
              placeholder="Select dates"
              minDate={MIN_DATE}
              maxDate={today}
              clearable
            />
          </View>

          <View style={styles.field}>
            <Input
              value={emailDraft}
              onChangeText={(text) => {
                setEmailDraft(text);
                updateFilter("userEmail", text.trim().length > 0 ? text.trim() : null);
              }}
              textInputProps={{
                keyboardType: "email-address",
                autoCapitalize: "none",
                autoCorrect: false,
              }}
              placeholder="All users"
              label="Email"
              clearable
              onClear={() => {
                setEmailDraft("");
                updateFilter("userEmail", null);
              }}
            />
          </View>
        </View>
      : null}
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["4xl"],
    ...Platform.select({
      web: {
        position: "relative",
        zIndex: theme.zIndex.inputOpen,
      } as Record<string, unknown>,
    }),
  },
  toggleBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: theme.spacing.lg,
  },
  row: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing.lg,
    paddingBottom: theme.spacing.lg,
    alignItems: "flex-start",
    ...Platform.select({
      web: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        paddingVertical: theme.spacing.lg,
        zIndex: theme.zIndex.inputOpen,
      } as Record<string, unknown>,
    }),
  },
  field: {
    minWidth: 240,
    flex: 1,
    ...Platform.select({
      web: { zIndex: "auto" as unknown as number },
    }),
  },
}));
